import type { MultiSelectOption } from "@dune/types";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Loader,
	ResizableLayout,
	Separator,
	TextArea,
	cn,
} from "@dune/ui";
import { Button } from "@dune/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@dune/ui";
import { Checkbox, Input, MultipleSelector } from "@dune/ui";
import {
	humanProcessingStatusDropdownClassMap,
	humanProcessingStatusLabels,
	humanProcessingStatuses,
	metadataKeyToLabelMap,
	metadataMultiselectOptions,
} from "@dune/vars";
import { useQueryClient } from "@tanstack/react-query";
import { XClose } from "@untitled-ui/icons-react";
import { cloneDeep } from "lodash";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import {
	useUpdateFileMetadata,
	useUpdateHumanProcessingStatus,
} from "../../api/mutations/files.mutations";
import {
	useGetFileMetadataDetails,
	useGetFileUrlByBucketAndKey,
	useGetFileWithMetadata,
} from "../../api/queries/files.queries";
import { useMe } from "../../api/queries/users.queries";
import { filesQueryKeys } from "../../api/query-keys/files.query-keys";
import { useDocumentStore } from "../../stores/document.store";
import { DocumentViewer } from "../common/DocumentViewer";
import { FileTypeAndIssuerSelect } from "../common/files/FileTypeAndIssuerSelect";
import { DebugMetadataDisplay } from "./DebugMetadataDisplay";

type FileWithMetadataPreviewWrapperProps = {
	children: React.ReactNode;
};

export const FileWithMetadataPreviewWrapper = ({
	children,
}: FileWithMetadataPreviewWrapperProps) => {
	const { fileId } = useDocumentStore();
	const { data: fileData } = useGetFileWithMetadata(fileId ?? "");
	const clearDocument = useDocumentStore((state) => state.clearDocument);
	const { name } = fileData || {};

	return (
		<ResizableLayout>
			<ResizableLayout.Main>{children}</ResizableLayout.Main>
			{fileData && (
				<ResizableLayout.Panel>
					<ResizableLayout.PanelHeader>
						<ResizableLayout.PanelTitle>{name}</ResizableLayout.PanelTitle>
						<div className="flex items-center gap-2">
							<FileWithMetadataPreviewPanelActions fileId={fileId} />
							<Button variant="ghost" onClick={clearDocument}>
								<XClose />
							</Button>
						</div>
					</ResizableLayout.PanelHeader>
					<ResizableLayout.PanelContent>
						<FileWithMetadataPreviewPanel fileId={fileId} />
					</ResizableLayout.PanelContent>
				</ResizableLayout.Panel>
			)}
		</ResizableLayout>
	);
};

export const FileWithMetadataPreviewPanelActions = ({
	fileId,
	onDelete,
}: {
	fileId?: string | null;
	onDelete?: (documentId: string) => void;
}) => {
	const { data: fileData } = useGetFileWithMetadata(fileId ?? "");

	const { me } = useMe();

	const isDune = me?.role === "DUNE";

	const {
		mutate: updateHumanProcessingStatusMutation,
		isPending: isUpdatingHumanProcessingStatus,
	} = useUpdateHumanProcessingStatus(fileId ?? "");
	const queryClient = useQueryClient();

	if (!fileData || !isDune) {
		return null;
	}

	const handleDelete = () => {
		if (fileId && onDelete) {
			onDelete(fileId);
		}
	};

	return (
		<div className="flex items-center gap-2">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						className={cn(
							humanProcessingStatusDropdownClassMap[
								fileData.humanProcessingStatus
							],
						)}
					>
						{humanProcessingStatusLabels[fileData.humanProcessingStatus]}
						{isUpdatingHumanProcessingStatus ? (
							<Loader className="w-4 h-4 animate-spin" />
						) : (
							<ChevronDown />
						)}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					{humanProcessingStatuses.map((status) => (
						<DropdownMenuItem
							key={status}
							onClick={() =>
								fileId &&
								updateHumanProcessingStatusMutation(status, {
									onSuccess: () => {
										queryClient.invalidateQueries({
											queryKey: filesQueryKeys.fileWithMetadata(fileId),
										});
									},
								})
							}
						>
							{humanProcessingStatusLabels[status]}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>

			{onDelete && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="icon">
							<span className="text-lg leading-none">⋯</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						<DropdownMenuItem
							onClick={handleDelete}
							className="text-red-600 hover:text-red-700"
						>
							Supprimer
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
};

export const FileWithMetadataPreviewPanel = ({
	fileId,
}: {
	fileId?: string | null;
}) => {
	const { me } = useMe();
	const iAmDune = me?.role === "DUNE";

	const {
		data: fileMetadataDetails,
		isLoading: isLoadingDetails,
		isError,
		error,
		refetch: refetchMetadataDetails,
	} = useGetFileMetadataDetails(fileId ?? "");
	const { data: fileData } = useGetFileWithMetadata(fileId ?? "");
	const { data: fileUrl } = useGetFileUrlByBucketAndKey(
		fileData?.bucket,
		fileData?.key,
	);
	const [tab, setTab] = useState("preview");

	return (
		<Tabs
			value={tab}
			onValueChange={(value) => setTab(value)}
			className="h-full w-full"
		>
			<TabsList>
				<TabsTrigger value="preview">Aperçu</TabsTrigger>
				<TabsTrigger value="details">Détails</TabsTrigger>
			</TabsList>
			<TabsContent value="preview">
				<DocumentViewer
					fileId={fileId ?? undefined}
					url={fileUrl}
					mimeType={fileData?.mimeType}
					fileName={fileData?.name}
				/>
			</TabsContent>
			<TabsContent value="details">
				{fileId && (
					<EditableMetadataDisplay
						fileId={fileId}
						refetch={refetchMetadataDetails}
					/>
				)}
				<Separator className="my-2" />

				{iAmDune && (
					<DebugMetadataDisplay
						isLoading={isLoadingDetails}
						isError={isError}
						error={error}
						fileMetadataDetails={fileMetadataDetails}
					/>
				)}
			</TabsContent>
		</Tabs>
	);
};

interface EditableMetadataDisplayProps {
	fileId: string;
	refetch: () => void;
}

const EditableMetadataDisplay = ({ fileId }: EditableMetadataDisplayProps) => {
	const { data: fileData } = useGetFileWithMetadata(fileId ?? "");

	const [localMetadata, setLocalMetadata] = useState<Record<string, unknown>>(
		{},
	);
	const [comment, setComment] = useState("");

	const { me } = useMe();
	const iAmDune = me?.role === "DUNE";

	useEffect(() => {
		setLocalMetadata(
			(fileData?.fileData?.mutableMetadata ?? {}) as Record<string, unknown>,
		);
		setComment(fileData?.fileData?.comment ?? "");
		return () => {
			setLocalMetadata({});
			setComment("");
		};
	}, [fileData]);

	const handleChange = (
		label: string,
		value: unknown,
		parentKey?: string,
		indexInArray?: number,
	) => {
		setLocalMetadata((prev) => {
			const newObj = cloneDeep(prev);
			if (parentKey) {
				if (indexInArray) {
					const newObjParent = newObj[parentKey] as Record<string, unknown>;
					const newObjParentArray = newObjParent[indexInArray] as Record<
						string,
						unknown
					>;
					newObjParentArray[label] = value;
				} else {
					(newObj[parentKey] as Record<string, unknown>)[label] = value;
				}
			} else {
				newObj[label] = value;
			}
			return newObj;
		});
	};

	const { mutate: updateFileMetadataMutation, isPending: isSaving } =
		useUpdateFileMetadata(fileId);

	const handleSave = async () => {
		updateFileMetadataMutation({
			mutableMetadata: localMetadata,
			comment,
		});
	};

	const renderInput = (
		currentKey: string,
		value: unknown,
		parentKey?: string,
		indexInArray?: number,
	) => {
		const inputId = `input-${currentKey}`;

		if (typeof value === "object" && !Array.isArray(value) && value !== null) {
			return Object.entries(value).map(([childKey, childValue]) => {
				const objectKey =
					indexInArray !== undefined ? childKey : `${currentKey}.${childKey}`;
				return (
					<div key={`input-${childKey}`} className="pl-2">
						<label
							htmlFor={`input-${childKey}`}
							className="block font-semibold mb-1"
						>
							{metadataKeyToLabelMap[objectKey]}
						</label>
						{renderInput(childKey, childValue, currentKey, indexInArray)}
					</div>
				);
			});
		}
		if (Array.isArray(value)) {
			if (value.every((arrayElement) => typeof arrayElement === "string")) {
				const options: MultiSelectOption[] = metadataMultiselectOptions.map(
					(optionLabel) => ({
						value: optionLabel,
						label: optionLabel,
					}),
				);
				return (
					<MultipleSelector
						options={options}
						value={value.map((arrayElement) => ({
							value: arrayElement,
							label: arrayElement,
						}))}
						onChange={(selected) =>
							handleChange(
								currentKey,
								selected.map((selectedElement) => selectedElement.value),
								parentKey,
								indexInArray,
							)
						}
						placeholder="Sélectionner..."
					/>
				);
			}
			if (
				value.every(
					(arrayElement) =>
						typeof arrayElement === "object" &&
						!Array.isArray(arrayElement) &&
						arrayElement !== null,
				)
			) {
				return (
					<div className="pl-2 flex flex-col gap-4">
						{value.map((childValue, index) => (
							<div key={`input-${currentKey}-${childValue}`}>
								{renderInput(currentKey, childValue, currentKey, index)}
							</div>
						))}
					</div>
				);
			}
		}
		if (typeof value === "string") {
			return (
				<Input
					id={inputId}
					type="text"
					value={value}
					onChange={(e) => {
						handleChange(currentKey, e.target.value, parentKey, indexInArray);
					}}
					className="w-full"
				/>
			);
		}
		if (typeof value === "number") {
			return (
				<Input
					id={inputId}
					type="number"
					value={value}
					onChange={(e) =>
						handleChange(
							currentKey,
							Number(e.target.value),
							parentKey,
							indexInArray,
						)
					}
					className="w-full"
				/>
			);
		}
		if (typeof value === "boolean") {
			return (
				<Checkbox
					id={inputId}
					checked={!!value}
					onCheckedChange={(checked) =>
						handleChange(currentKey, checked, parentKey, indexInArray)
					}
				/>
			);
		}
		return <span>Champ non pris en charge</span>;
	};

	return (
		<div className="flex flex-col gap-4 p-2">
			{iAmDune && (
				<>
					<FileTypeAndIssuerSelect fileId={fileId} />
					{Object.entries(localMetadata).length > 0 && (
						<>
							<Separator className="my-2" />
							{Object.entries(localMetadata).map(([key, value]) => (
								<div key={key} className="mb-2">
									<label
										htmlFor={`input-${key}`}
										className="block font-semibold mb-1"
									>
										{metadataKeyToLabelMap[key]}
									</label>
									{renderInput(key, value)}
								</div>
							))}
						</>
					)}
				</>
			)}
			{iAmDune ? (
				<TextArea
					label="Commentaire"
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					className="min-h-[120px] align-top"
				/>
			) : (
				<div>
					<div className="font-semibold mb-1">Commentaire</div>
					<div className="whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-4">
						{comment || "-"}
					</div>
				</div>
			)}
			{iAmDune && (
				<Button onClick={handleSave} disabled={isSaving} className="mt-4">
					{isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
				</Button>
			)}
		</div>
	);
};
